import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLES } from "@/lib/permissions";

const FIELDS = [
  { name: "name", label: "Company Name" },
  { name: "tax_id", label: "Tax ID" },
  { name: "address", label: "Address" },
  { name: "phone", label: "Phone" },
  { name: "email", label: "Contact Email" },
  { name: "financial_year_start", label: "Financial Year Start (YYYY-MM-DD)" },
  { name: "base_currency", label: "Base Currency" },
];

export default function CompanyProfilePage() {
  const { user } = useAuth();
  const isAdmin = user?.role === ROLES.SYSTEM_ADMIN || user?.full_access === true;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data } = await api.get("/company");
        setForm(data);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...form };
      delete payload.id;
      delete payload.updated_at;
      delete payload.logo_url;
      const { data } = await api.patch("/company", payload);
      setForm(data);
      toast.success("Company profile updated.");
    } catch (e2) {
      toast.error(formatApiError(e2));
    } finally {
      setSaving(false);
    }
  };

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="company-loading">
        <Loader2 className="h-6 w-6 animate-spin text-[#4FA3D1]" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl" data-testid="company-page">
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle>Company Profile</CardTitle>
          <CardDescription>
            {isAdmin
              ? "Update SOM-TURK PLC company information. Changes are recorded in the audit log."
              : "Company information (read-only — only the System Administrator can edit)."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSave} className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {FIELDS.map((f) => (
              <div key={f.name} className={f.name === "address" ? "md:col-span-2" : ""}>
                <Label htmlFor={f.name}>{f.label}</Label>
                <Input
                  id={f.name}
                  name={f.name}
                  value={form[f.name] || ""}
                  onChange={onChange}
                  disabled={!isAdmin}
                  className="mt-1 h-11"
                  data-testid={`company-${f.name}-input`}
                />
              </div>
            ))}

            {/* Phase 24-b (2026-07-13) — Report Logo picker */}
            <div className="md:col-span-2 border-t border-slate-200 pt-5 mt-2">
              <Label className="text-xs uppercase tracking-wider text-slate-500 mb-3 block">
                Branding — Report Logo
              </Label>
              <div className="flex flex-col sm:flex-row gap-4 items-start">
                <div className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg">
                  <img src="/logo/somturk_white.png" alt="White background"
                        className="w-24 h-24 object-contain bg-white rounded" />
                  <img src="/logo/somturk_transparent.png" alt="Transparent"
                        className="w-24 h-24 object-contain bg-slate-100 rounded"
                        style={{ backgroundImage: "linear-gradient(45deg,#e5e7eb 25%,transparent 25%),linear-gradient(-45deg,#e5e7eb 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e5e7eb 75%),linear-gradient(-45deg,transparent 75%,#e5e7eb 75%)", backgroundSize: "12px 12px", backgroundPosition: "0 0,0 6px,6px -6px,-6px 0px" }} />
                </div>
                <div className="flex-1 space-y-2">
                  {[
                    ["auto", "Auto — choose based on page background (default)"],
                    ["transparent", "Force transparent variant"],
                    ["white", "Force white-background variant"],
                  ].map(([val, label]) => (
                    <label key={val} className="flex items-center gap-2 cursor-pointer text-sm">
                      <input type="radio" name="logo_variant" value={val}
                              checked={(form.logo_variant || "auto") === val}
                              onChange={onChange}
                              disabled={!isAdmin}
                              data-testid={`logo-variant-${val}`}
                              className="h-4 w-4" />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>

            {isAdmin && (
              <div className="md:col-span-2 flex justify-end pt-2">
                <Button
                  type="submit"
                  disabled={saving}
                  className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
                  data-testid="company-save-button"
                >
                  {saving ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      Save changes
                    </>
                  )}
                </Button>
              </div>
            )}
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
